/**
 * Org-wide scheduling defaults — the single source of truth for the six
 * `ahoy_scheduling_default_*` properties stored on the `ahoy_org` entity.
 *
 * Two consumers share this module, and they must not drift:
 *
 *  - Settings → Meeting assistant, which *writes* the defaults.
 *  - The booking-link create form, which *reads* them as its initial values.
 *
 * Adding a seventh default means adding it to
 * `ORG_SCHEDULING_DEFAULT_PROPERTIES` and to `ORG_DEFAULT_FIELD_MAP`. The
 * `satisfies` constraint below makes omitting the second step a type error
 * rather than a setting that saves and silently does nothing.
 */

import { parseLocationTypeKind } from '@/lib/crm/utils/locationType';
import {
  parseWorkingHours,
  serializeWorkingHours,
} from '@/lib/crm/utils/workingHours';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

/**
 * Every org-level scheduling default property, in the order the settings form
 * renders them.
 */
export const ORG_SCHEDULING_DEFAULT_PROPERTIES = [
  'ahoy_scheduling_default_working_hours',
  'ahoy_scheduling_default_buffer_before_minutes',
  'ahoy_scheduling_default_buffer_after_minutes',
  'ahoy_scheduling_default_min_notice_hours',
  'ahoy_scheduling_default_max_days_ahead',
  'ahoy_scheduling_default_location_type',
] as const;

export type OrgSchedulingDefaultProperty =
  (typeof ORG_SCHEDULING_DEFAULT_PROPERTIES)[number];

interface OrgDefaultFieldMapping {
  /** The booking-type property this org default seeds. */
  readonly bookingTypeProperty: string;
  /** Normalizes the raw CRM property value into the form's value shape. */
  readonly toFormValue: (raw: unknown) => unknown;
}

function toFiniteNumber(raw: unknown): number | undefined {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Maps each org default onto the booking-type property it seeds.
 *
 * `satisfies Record<OrgSchedulingDefaultProperty, …>` is load-bearing: it fails
 * the build if a property is added to `ORG_SCHEDULING_DEFAULT_PROPERTIES`
 * without a mapping here.
 */
const ORG_DEFAULT_FIELD_MAP = {
  ahoy_scheduling_default_working_hours: {
    bookingTypeProperty: 'ahoy_working_hours',
    // parseWorkingHours accepts both the JSON string and the already-parsed
    // object the CRM store may hand back, and falls back to the standard
    // week when the stored value is unusable.
    toFormValue: raw => serializeWorkingHours(parseWorkingHours(raw)),
  },
  ahoy_scheduling_default_buffer_before_minutes: {
    bookingTypeProperty: 'ahoy_buffer_before_minutes',
    toFormValue: toFiniteNumber,
  },
  ahoy_scheduling_default_buffer_after_minutes: {
    bookingTypeProperty: 'ahoy_buffer_after_minutes',
    toFormValue: toFiniteNumber,
  },
  ahoy_scheduling_default_min_notice_hours: {
    bookingTypeProperty: 'ahoy_min_notice_hours',
    toFormValue: toFiniteNumber,
  },
  ahoy_scheduling_default_max_days_ahead: {
    bookingTypeProperty: 'ahoy_max_days_ahead',
    toFormValue: toFiniteNumber,
  },
  ahoy_scheduling_default_location_type: {
    bookingTypeProperty: 'ahoy_location_type',
    toFormValue: parseLocationTypeKind,
  },
} as const satisfies Record<
  OrgSchedulingDefaultProperty,
  OrgDefaultFieldMapping
>;

/**
 * Translates the org entity's scheduling defaults into booking-type form
 * values, ready to spread over a booking link's hardcoded initial values.
 *
 * Properties the org has never set are omitted rather than emitted as
 * `undefined`, so the caller's own defaults survive the spread.
 */
export function mapOrgDefaultsToBookingTypeValues(
  orgEntity: CrmObject | undefined
): Record<string, unknown> {
  if (!orgEntity) {
    return {};
  }

  const values: Record<string, unknown> = {};
  for (const property of ORG_SCHEDULING_DEFAULT_PROPERTIES) {
    const raw = getPropertyValue<unknown>(orgEntity.properties?.[property]);
    if (raw === null || raw === undefined || raw === '') {
      continue;
    }
    const { bookingTypeProperty, toFormValue } =
      ORG_DEFAULT_FIELD_MAP[property];
    const formValue = toFormValue(raw);
    if (formValue !== undefined) {
      values[bookingTypeProperty] = formValue;
    }
  }
  return values;
}
